import { useLazyQuery } from "@apollo/client";
import { Checkbox, createStyles, FormControlLabel, Grid, makeStyles, RadioGroup, Theme } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "components/Dialog";
import { TextField } from "components/TextField";
import HTTP_STATUS_CODES from "constants/http-status-code";
import useText from "constants/text";
import {
  ParcelRadio,
  ParcelRadioD,
  ParcelRadioL,
  ParcelRadioM,
  ParcelRadioXL,
  ParcelRadioXXL,
} from "domains/Task/components/Job/Radio";
import { Client, Job, JobType, Order, Parcel, ParcelCreationInput, ParcelSize } from "generated/graphql";
import { ALL_CLIENT_REF_ADDRESS_BY_CLIENT_ID } from "graphql/clientRef/queries";
import { useCurrentClient } from "hooks";
import { DocumentIcon, SizedIconL, SizedIconM, SizedIconS, SizedIconXL, SizedIconXXL } from "icons";
import _ from "lodash";
import React from "react";
import "styled-components/macro";
import Autocomplete from "ui/Autocomplete";
import { uuid } from "utils";

// eslint-disable-next-line import/extensions

export const validJobTypes = [JobType.Dropoff, JobType.FromStock];

interface IProps {
  order?: Order;
  open?: boolean;
  handleClose?: () => void;
  user?: Client;
  handleAddParcel?: (order: any) => void;
  handleOnEditedParcel?: (editedParcel: any) => void;
  isEdited?: boolean;
  parcel?: Parcel;
  job?: Job;
}

const defaultValues: ParcelCreationInput | Parcel = {
  parcel_description: "",
  parcel_additional_info: "",
  parcel_size: ParcelSize.S,
  parcel_client_reference: "",
  parcel_value: 0.0,
  parcel_amount_to_collect: 0.0,
  pickupAddress: null as object,
};

const defaultInputValidation = {
  parcel_description: false,
  parcel_client_reference: false,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    icons: {
      width: "80px !important",
      height: "80px !important",
      [theme.breakpoints.down("xs")]: { width: "53px !important", height: "52px !important" },
    },
    grid: {
      height: "52vh",
    },
    radioGroup: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    pickupAdress: { marginBottom: "40px", [theme.breakpoints.down("xs")]: { marginTop: -18 } },
    textfield: { [theme.breakpoints.down("xs")]: { top: -12, position: "relative" } },
    labelPickupAdress: {
      padding: "0 16px",
      [theme.breakpoints.down("xs")]: { top: -13, position: "relative", textAlign: "start" },
    },
  })
);

export default function NewParcel({
  open,
  handleClose,
  order,
  parcel,
  handleAddParcel,
  handleOnEditedParcel,
  user,
  isEdited,
  job,
}: IProps) {
  const styles = useStyles();

  const { text } = useText();
  const [formValues, setFormValues] = React.useState(defaultValues);
  const [isFormSubmited, setFormSubmited] = React.useState(false);
  const [parcels, setParcels] = React.useState([]);
  const [differentPickupAddresses, setDifferentPickupAddresses] = React.useState(false);
  const [addresses, setAddresses] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [error, setError] = React.useState(defaultInputValidation);
  const [allClientRefAddressByClientId, { loading, data }] = useLazyQuery(ALL_CLIENT_REF_ADDRESS_BY_CLIENT_ID);
  const { client } = useCurrentClient();

  React.useEffect(() => {
    if (isEdited) {
      setFormValues({
        ...parcel,
      });
    }
  }, [open, parcel]);
  React.useEffect(() => {
    if (order && order.order_parcels.length > 0) {
      setParcels(order.order_parcels);
    }
  }, [order]);

  const handleConfirm = () => {
    if (isEdited) {
      handleOnEditedParcel(formValues);
    } else {
      parcels.push({ ...formValues, _id: uuid() });
      const ordersData = { ...order, order_parcels: parcels };
      handleAddParcel({ ...ordersData });
    }
    setFormValues(defaultValues);
  };

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = event.target;

      if (value) {
        setError({ ...error, [name]: value.match(/[^A-Za-z0-9À-ÿ.,'œ ]+/) });
      }
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value,
      });
    },
    [formValues]
  );

  const handleOnPickupAddressesChange = (event: any, newValue: any) => {
    const pickupMoreInfoObject = {
      firstName: newValue?.firstname,
      lastName: newValue?.lastname,
      phoneNumber: newValue?.phone_number,
    };
    setFormValues({
      ...formValues,
      pickupAddress: _.omit(newValue, ["default", "email", "lastname", "phone_number", "address.country"]),
      pickupMoreInfo: pickupMoreInfoObject,
    });
  };
  React.useEffect(() => {
    allClientRefAddressByClientId({ variables: { params: { clientID: client?._id } } });
  }, [open, differentPickupAddresses]);

  React.useEffect(() => {
    const result = data?.results;
    if (result?.code === HTTP_STATUS_CODES.OK) {
      const addresses = result?.data.map((address: any) => address);
      setAddresses(addresses);
    }
  }, [data]);

  const handleOnInputChange = (event: any, newInputValue: React.SetStateAction<string>) => {
    setInputValue(newInputValue);
  };

  const handleCheckboxChange = () => {
    setDifferentPickupAddresses(!differentPickupAddresses);
  };

  const isValidJobType = React.useMemo<boolean>(() => validJobTypes.includes(job?.job_type), [job?.job_type]);

  return (
    <Dialog
      title={parcel ? text.parcel.parcel_update : text.all.add_parcel}
      open={open}
      confirmButtonTitle={parcel ? text.all.edit : text.all.add}
      disableConfirmButton={
        !formValues.parcel_description || (differentPickupAddresses && !formValues?.pickupAddress?.address)
      }
      handleClose={handleClose}
      handleConfirm={handleConfirm}
      maxWidth="md"
      height="77vh"
    >
      <Grid item xs={12}>
        <label
          css={`
            color: #707070;
          `}
        >
          {text.all.size}
        </label>
      </Grid>
      <Grid container spacing={4} className={styles.grid}>
        <Grid item xs={12}>
          <RadioGroup
            row
            aria-label="parcel_size"
            name="parcel_size"
            value={formValues.parcel_size}
            onChange={handleInputChange}
            className={styles.radioGroup}
          >
            <Tooltip title="Document, couriel">
              <ParcelRadioD value="D" icon={<DocumentIcon />} checkedIcon={<DocumentIcon color="#399ebf" />} />
            </Tooltip>
            <Tooltip title="S (max 5kg) ex: Laptop, cell phone, cloth, soap jar">
              <ParcelRadio
                className={styles.icons}
                value="S"
                icon={<SizedIconS />}
                checkedIcon={<SizedIconS color="#399ebf" />}
              />
            </Tooltip>
            <Tooltip title="M (max 30kg) ex: Small electrical appliances (microwave, fridge bar, cardboard, TV)">
              <ParcelRadioM
                className={styles.icons}
                value="M"
                icon={<SizedIconM />}
                checkedIcon={<SizedIconM color="#399ebf" />}
              />
            </Tooltip>
            <Tooltip title="L (max 60kg) ex: Big electrical appliances (fridge, gas cooker, washing machine)">
              <ParcelRadioL
                className={styles.icons}
                value="L"
                icon={<SizedIconL />}
                checkedIcon={<SizedIconL color="#399ebf" />}
              />
            </Tooltip>
            <Tooltip title="XL (max 100kg) ex: Bedroom">
              <ParcelRadioXL
                className={styles.icons}
                value="XL"
                icon={<SizedIconXL />}
                checkedIcon={<SizedIconXL color="#399ebf" />}
              />
            </Tooltip>
            <Tooltip title="XL (max 100kg) ex: Bedroom">
              <ParcelRadioXXL
                className={styles.icons}
                value="XXL"
                icon={<SizedIconXXL />}
                checkedIcon={<SizedIconXXL color="#399ebf" />}
              />
            </Tooltip>
          </RadioGroup>
        </Grid>
        <Grid item xs={12} sm={6} className={styles.textfield}>
          <TextField
            fullWidth
            label={text.all.description}
            name="parcel_description"
            value={formValues.parcel_description}
            onChange={handleInputChange}
            error={isFormSubmited && formValues.parcel_description.length === 0}
            helperText={
              isFormSubmited && formValues.parcel_description.length === 0 ? `${text.all.this_field_is_required}` : ""
            }
            required
          />
        </Grid>

        <Grid item xs={12} sm={6} className={styles.textfield}>
          <TextField
            fullWidth
            label={text.client.client_reference}
            name="parcel_client_reference"
            value={formValues.parcel_client_reference}
            onChange={handleInputChange}
          />
        </Grid>

        <Grid item xs={12} sm={6} className={styles.textfield}>
          <TextField
            fullWidth
            label={text.all.price}
            type="number"
            name="parcel_value"
            value={formValues?.parcel_value}
            onChange={handleInputChange}
            error={isFormSubmited && (String(formValues?.parcel_value).length === 0 || formValues?.parcel_value < 0)}
            helperText={
              isFormSubmited && (String(formValues?.parcel_value).length === 0 || formValues?.parcel_value < 0)
                ? `${text.all.this_field_is_required}`
                : ""
            }
            required
            inputProps={{
              min: 0,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} className={styles.textfield}>
          <TextField
            fullWidth
            minRows={4}
            type="number"
            label={text.all.amount_to_collect}
            name="parcel_amount_to_collect"
            value={formValues.parcel_amount_to_collect}
            onChange={handleInputChange}
            error={
              (isFormSubmited &&
                (String(formValues.parcel_amount_to_collect).length === 0 || formValues.parcel_amount_to_collect) <
                  0) ||
              String(formValues?.parcel_amount_to_collect).length > 10
            }
            helperText={
              isFormSubmited &&
              (String(formValues.parcel_amount_to_collect).length === 0 || formValues.parcel_amount_to_collect) < 0
                ? `${text.all.this_field_is_required}`
                : "" || String(formValues?.parcel_amount_to_collect).length > 10
                ? `${text.all.amount_error}`
                : ""
            }
            required
            inputProps={{
              min: 0,
            }}
          />
        </Grid>
        <Grid item xs={12} className={styles.textfield}>
          <TextField
            fullWidth
            label={text.all.additionals_infos}
            name="parcel_additional_info"
            value={formValues.parcel_additional_info}
            onChange={handleInputChange}
          />
        </Grid>
        {!isValidJobType && (
          <>
            <Grid item xs={12} className={styles.labelPickupAdress}>
              <FormControlLabel
                label={text.parcel.different_pick_up_addresses}
                control={<Checkbox checked={differentPickupAddresses} onChange={handleCheckboxChange} />}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={styles.pickupAdress}
                disabled={!differentPickupAddresses}
                name={text.parcel.different_pick_up_addresses}
                options={addresses}
                value={formValues.pickupAddress}
                onChange={handleOnPickupAddressesChange}
                inputValue={inputValue}
                onInputChange={handleOnInputChange}
                label={text.parcel.different_pick_up_addresses}
                getOptionLabel={(option: any) => `${option?.address?.address}`}
                renderInput={(params: any) => <TextField {...params} variant="outlined" />}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Dialog>
  );
}
